import config from './load-config';

/**
 * Note: password strength validation is recommended for customers creating new passwords.
 * *NOT* for customer authentication!!!
 */

const max = 256;
const min = config.accounts.signup.passwordStrength.minLength;
const requiredTypes =
  config.accounts.signup.passwordStrength.minCharacterClasses;

export const isDifferentFromEmail = (password, email) => {
  return password !== email;
};

export const maxLength = password => {
  return password.length > max;
};

export const minLength = password => {
  return password.length < min;
};

export const whiteSpace = password => {
  return password.trim().length !== password.length;
};

// TODO: see https://www.solarwindsmsp.com/blog/nist-password-standards2
// overly complex passwords can lead to poor password behavior.
// Users who forget their complicated passwords tend to end up
// replacing them with new, weaker ones.
export const isComplexEnough = (password: string) => {
  const count = {
    lower: 0,
    upper: 0,
    digit: 0,
    special: 0,
  };

  password.split('').map(char => {
    if (/[a-z]/.test(char)) count.lower++;
    else if (/[A-Z]/.test(char)) count.upper++;
    else if (/\d/.test(char)) count.digit++;
    else if (/\S/.test(char)) count.special++;
  });

  return Object.values(count).filter(Boolean).length >= requiredTypes;
};

/**
 * Combining the following for the sake of convenience.
 *
 * @see \Magento\Customer\Model\AccountManagement::checkPasswordStrength
 * @see \Magento\Customer\Model\Customer\CredentialsValidator::checkPasswordDifferentFromEmail
 */
const checkPasswordStrength = (
  password: string,
  email: string | null = null
) => {
  const strength = {
    pass: false,
    message: '',
  };

  /**
   * TODO: customize messages once we have our own preferences.
   * Might want to load these from some sort of translations file.
   *
   * For now just using the Magento defaults.
   */
  if (!password) {
    return strength;
  } else if (email && !isDifferentFromEmail(password, email)) {
    strength.message =
      "The password can't be the same as the email address. " +
      'Create a new password and try again.';
  } else if (maxLength(password)) {
    strength.message = `Please enter a password with at most ${max} characters.`;
  } else if (minLength(password)) {
    strength.message =
      `The password needs at least ${min} characters. ` +
      'Create a new password and try again.';
  } else if (whiteSpace(password)) {
    strength.message =
      "The password can't begin or end with a space. " +
      'Verify the password and try again.';
  } else if (!isComplexEnough(password)) {
    strength.message =
      'Minimum of different classes of characters in password is ' +
      `${requiredTypes}. Classes of characters: Lower Case, Upper Case, Digits, ` +
      'Special Characters.';
  } else {
    strength.pass = true;
  }

  return strength;
};

export default checkPasswordStrength;
